import { ApiTagTypes, innovorderApi } from '../index';
import { ApiRequest, ApiResponse, TokenRequest } from '../request.types';
import {
    AllowedIpsResponse,
    AuthUser,
    BrandGuestLoginPayload,
    ForgotPasswordPayload,
    LoginAsGuestPayload,
    LoginAsGuestResponse,
    LoginPayload,
    LoginResponse,
    LoginResponseData,
    MfaConfirmTotpResponse,
    MfaConfirmWebauthnResponse,
    MfaMethodsListResponse,
    MfaSetupTotpResponse,
    MfaSetupWebauthnOptionsResponse,
    MfaVerifyResponse,
    MfaVerifyWebauthnOptionsResponse,
    TotpCredentialCreateResponse,
    TotpSetupOptionsResponse,
    WebauthnAssertion,
    WebauthnAttestation,
    WebauthnCredentialCreateResponse,
    AuthResponseData,
    ResetPasswordPayload,
    VerifyForgotPasswordTokenPayload,
} from './auth.type';

export type SamlProviderByEmailResponse = {
    uuid: string;
};

const authApi = innovorderApi.injectEndpoints({
    endpoints: (build) => ({
        login: build.mutation<LoginResponseData, LoginPayload>({
            query: ({ email, password, rememberMe, locale, captchaToken }) => ({
                url: `oauth/login`,
                method: 'POST',
                body: {
                    grant_type: 'password',
                    username: email,
                    locale,
                    password,
                    rememberMe,
                    ...(captchaToken ? { captchaToken } : {}),
                },
            }),
            transformResponse: (response: LoginResponse) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        verifyMfa: build.mutation<MfaVerifyResponse, { token: string; code: string }>({
            query: ({ token, code }) => ({
                url: `oauth/mfa/verify`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { code },
            }),
            transformResponse: (response: ApiResponse<MfaVerifyResponse>) => response.data,
        }),
        verifyMfaBackupCode: build.mutation<MfaVerifyResponse, { token: string; code: string }>({
            query: ({ token, code }) => ({
                url: `oauth/mfa/backup/verify`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { code },
            }),
            transformResponse: (response: ApiResponse<MfaVerifyResponse>) => response.data,
        }),
        setupTotp: build.mutation<MfaSetupTotpResponse, { token: string }>({
            query: ({ token }) => ({
                url: `oauth/mfa/setup/totp`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<MfaSetupTotpResponse>) => response.data,
        }),
        confirmTotp: build.mutation<MfaConfirmTotpResponse, { token: string; code: string }>({
            query: ({ token, code }) => ({
                url: `oauth/mfa/setup/totp/confirm`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { code },
            }),
            transformResponse: (response: ApiResponse<MfaConfirmTotpResponse>) => response.data,
        }),
        setupWebauthnOptions: build.mutation<MfaSetupWebauthnOptionsResponse, { token: string }>({
            query: ({ token }) => ({
                url: `oauth/mfa/setup/webauthn/options`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<MfaSetupWebauthnOptionsResponse>) => response.data,
        }),
        confirmWebauthn: build.mutation<
            MfaConfirmWebauthnResponse,
            { token: string; attestation: WebauthnAttestation; deviceName?: string }
        >({
            query: ({ token, attestation, deviceName }) => ({
                url: `oauth/mfa/setup/webauthn/confirm`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: deviceName ? { attestation, deviceName } : { attestation },
            }),
            transformResponse: (response: ApiResponse<MfaConfirmWebauthnResponse>) => response.data,
        }),
        verifyWebauthnOptions: build.mutation<MfaVerifyWebauthnOptionsResponse, { token: string }>({
            query: ({ token }) => ({
                url: `oauth/mfa/webauthn/options`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<MfaVerifyWebauthnOptionsResponse>) => response.data,
        }),
        verifyWebauthn: build.mutation<MfaVerifyResponse, { token: string; assertion: WebauthnAssertion }>({
            query: ({ token, assertion }) => ({
                url: `oauth/mfa/webauthn/verify`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { assertion },
            }),
            transformResponse: (response: ApiResponse<MfaVerifyResponse>) => response.data,
        }),
        getMfaMethods: build.query<MfaMethodsListResponse, { token: string; userId: number }>({
            query: ({ token, userId }) => ({
                url: `users/${userId}/mfa-methods`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<MfaMethodsListResponse>) => response.data,
            providesTags: [ApiTagTypes.MfaMethods],
        }),
        webauthnSetupOptionsForUser: build.mutation<
            MfaSetupWebauthnOptionsResponse & { registrationId: string },
            { token: string; userId: number }
        >({
            query: ({ token, userId }) => ({
                url: `users/${userId}/webauthn-credentials/options`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<MfaSetupWebauthnOptionsResponse & { registrationId: string }>) =>
                response.data,
        }),
        createWebauthnCredentialForUser: build.mutation<
            WebauthnCredentialCreateResponse,
            {
                token: string;
                userId: number;
                attestation: WebauthnAttestation;
                deviceName?: string;
                registrationId: string;
            }
        >({
            query: ({ token, userId, attestation, deviceName, registrationId }) => ({
                url: `users/${userId}/webauthn-credentials`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: deviceName ? { attestation, deviceName, registrationId } : { attestation, registrationId },
            }),
            transformResponse: (response: ApiResponse<WebauthnCredentialCreateResponse>) => response.data,
            invalidatesTags: [ApiTagTypes.MfaMethods],
        }),
        deleteWebauthnCredentialForUser: build.mutation<void, { token: string; userId: number; passkeyId: number }>({
            query: ({ token, userId, passkeyId }) => ({
                url: `users/${userId}/webauthn-credentials/${passkeyId}`,
                method: 'DELETE',
                headers: { authorization: `Bearer ${token}` },
            }),
            invalidatesTags: [ApiTagTypes.MfaMethods],
        }),
        getAllowedIps: build.query<AllowedIpsResponse, { token: string; userId: number }>({
            query: ({ token, userId }) => ({
                url: `users/${userId}/allowed-ips`,
                method: 'GET',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<AllowedIpsResponse>) => response.data,
            providesTags: [ApiTagTypes.AllowedIps],
        }),
        updateAllowedIps: build.mutation<AllowedIpsResponse, { token: string; userId: number; allowedIps: string[] }>({
            query: ({ token, userId, allowedIps }) => ({
                url: `users/${userId}/allowed-ips`,
                method: 'PUT',
                headers: { authorization: `Bearer ${token}` },
                body: { allowedIps },
            }),
            transformResponse: (response: ApiResponse<AllowedIpsResponse>) => response.data,
            invalidatesTags: [ApiTagTypes.AllowedIps],
        }),
        deleteMfaConfig: build.mutation<void, { token: string; configId: number }>({
            query: ({ token, configId }) => ({
                url: `oauth/mfa/${configId}`,
                method: 'DELETE',
                headers: { authorization: `Bearer ${token}` },
            }),
            invalidatesTags: [ApiTagTypes.MfaMethods],
        }),
        totpSetupOptionsForUser: build.mutation<TotpSetupOptionsResponse, { token: string; userId: number }>({
            query: ({ token, userId }) => ({
                url: `users/${userId}/totp-credentials/options`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
            }),
            transformResponse: (response: ApiResponse<TotpSetupOptionsResponse>) => response.data,
        }),
        createTotpForUser: build.mutation<
            TotpCredentialCreateResponse,
            { token: string; userId: number; code: string; setupId: string }
        >({
            query: ({ token, userId, code, setupId }) => ({
                url: `users/${userId}/totp-credentials`,
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: { code, setupId },
            }),
            transformResponse: (response: ApiResponse<TotpCredentialCreateResponse>) => response.data,
            invalidatesTags: [ApiTagTypes.MfaMethods],
        }),
        samlProviderByEmail: build.query<SamlProviderByEmailResponse, { email: string }>({
            query: ({ email }) => ({
                url: `module_saml_provider/email`,
                params: { email },
            }),
            transformResponse: (response: ApiResponse<SamlProviderByEmailResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        me: build.query<AuthUser, TokenRequest>({
            query: ({ token }) => ({
                url: `/oauth/me`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<AuthUser>) => response.data,
            extraOptions: { maxRetries: 3 },
            providesTags: [ApiTagTypes.User],
        }),
        logout: build.mutation<AuthUser, TokenRequest>({
            query: ({ token }) => ({
                url: `/oauth/logout`,
                method: 'GET',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: ApiResponse<AuthUser>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        loginAsGuest: build.query<LoginAsGuestResponse, LoginAsGuestPayload>({
            query: ({ userId }) => ({
                url: `oauth/admin`,
                params: { userId },
            }),
            transformResponse: (response: ApiResponse<LoginAsGuestResponse>) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        brandGuestLogin: build.query<AuthResponseData, ApiRequest<BrandGuestLoginPayload>>({
            query: ({ brandId, token }) => ({
                url: `brand_groups/${brandId}/user`,
                headers: {
                    authorization: `Bearer ${token}`,
                },
            }),
            transformResponse: (response: Omit<LoginResponse, 'data'> & { data: AuthResponseData }) => response.data,
            extraOptions: { maxRetries: 3 },
        }),
        forgotPassword: build.mutation<void, ForgotPasswordPayload>({
            query: (body) => ({
                url: `users/forgottenpassword`,
                method: 'POST',
                body,
            }),
        }),
        verifyForgotPasswordToken: build.query<void, VerifyForgotPasswordTokenPayload>({
            query: ({ token, userId }) => ({
                url: `users/forgottenpassword/${token}/${userId}`,
                method: 'GET',
            }),
        }),
        resetPassword: build.mutation<void, ResetPasswordPayload>({
            query: ({ token, userId, newPassword }) => ({
                url: `users/forgottenpassword/${token}/${userId}`,
                method: 'PUT',
                body: { newPassword },
            }),
        }),
        extendAccount: build.query<void, { token: string }>({
            query: ({ token }) => ({
                url: `users/extend-account`,
                method: 'GET',
                params: { token },
            }),
        }),
    }),
});

export const {
    useLoginMutation,
    useLazySamlProviderByEmailQuery,
    useLazyMeQuery,
    useMeQuery,
    useLogoutMutation,
    useLazyLoginAsGuestQuery,
    useLazyBrandGuestLoginQuery,
    useForgotPasswordMutation,
    useVerifyForgotPasswordTokenQuery,
    useResetPasswordMutation,
    useVerifyMfaMutation,
    useVerifyMfaBackupCodeMutation,
    useSetupTotpMutation,
    useConfirmTotpMutation,
    useSetupWebauthnOptionsMutation,
    useConfirmWebauthnMutation,
    useVerifyWebauthnOptionsMutation,
    useVerifyWebauthnMutation,
    useGetMfaMethodsQuery,
    useWebauthnSetupOptionsForUserMutation,
    useCreateWebauthnCredentialForUserMutation,
    useDeleteWebauthnCredentialForUserMutation,
    useDeleteMfaConfigMutation,
    useTotpSetupOptionsForUserMutation,
    useCreateTotpForUserMutation,
    useGetAllowedIpsQuery,
    useUpdateAllowedIpsMutation,
    useExtendAccountQuery,
} = authApi;
